import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteKmdActual,
  fetchKmdActuals,
  saveKmdActual,
  type KmdActualsRequestOptions,
} from '@/lib/kmdActuals';

/**
 * Query layer for the recorded KMD declarations (replit.md master rule — all
 * state flows through the shared query layer, so BOTH lenses read the same
 * month → actual map; L14). The map is keyed `YYYY-MM` → integer cents.
 */
export const kmdActualsQueryKey = ['kmd-actuals'] as const;

export function useKmdActuals(options?: KmdActualsRequestOptions): {
  /** month (`YYYY-MM`) → declared cents; empty while loading. */
  actuals: Record<string, number>;
  isLoading: boolean;
  isError: boolean;
} {
  const query = useQuery({
    queryKey: kmdActualsQueryKey,
    queryFn: () => fetchKmdActuals(options),
  });
  const actuals: Record<string, number> = {};
  for (const row of query.data ?? []) actuals[row.month] = row.actualCents;
  return { actuals, isLoading: query.isLoading, isError: query.isError };
}

/**
 * Record / correct / clear one month's declared figure. `actualCents: null`
 * clears the record (the cell falls back to the document backcast).
 */
export function useSaveKmdActual(options?: KmdActualsRequestOptions) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { month: string; actualCents: number | null }) => {
      if (vars.actualCents === null) {
        await deleteKmdActual(vars.month, options);
        return;
      }
      await saveKmdActual(vars.month, vars.actualCents, options);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kmdActualsQueryKey });
    },
  });
}
